import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { Expose, Type } from "class-transformer";
import { IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

type PatientMonitorConfigurationDetails = {
  name: string;
  familyNumber: string;
  family?: MonitoringProductItem;
  sensor?: MonitoringProductItem;
  mountingGroup?: MonitoringProductItem;
  mountingArm?: MonitoringProductItem;
  attachments?: MonitoringProductItem[];
};

type PatientMonitorConfigurationParams = PatientMonitorConfigurationDetails & {
  id: string;
  project: ScopedProject;
  category: MonitoringCategory;
};

type PatientMonitorConfigurationCreateParams = Omit<PatientMonitorConfigurationParams, "id">;

const DESERIALIZATION_PROJECT_PLACEHOLDER = {} as ScopedProject;

export class PatientMonitorConfiguration extends ProductConfiguration {
  @IsString()
  public familyNumber!: string;

  @IsEnum(MonitoringCategory)
  @Expose({ name: "category" })
  private _category!: MonitoringCategory;

  @Type(() => MonitoringProductItem)
  @IsOptional()
  @Expose({ name: "family" })
  @ValidateNested()
  private _family?: MonitoringProductItem;

  @Type(() => MonitoringProductItem)
  @IsOptional()
  @Expose({ name: "sensor" })
  @ValidateNested()
  private _sensor?: MonitoringProductItem;

  @Type(() => MonitoringProductItem)
  @IsOptional()
  @Expose({ name: "mountingGroup" })
  @ValidateNested()
  private _mountingGroup?: MonitoringProductItem;

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @Expose({ name: "mountingArm" })
  @ValidateNested()
  private _mountingArm?: MonitoringProductItem;

  @IsOptional()
  @Expose()
  @ValidateNested({ each: true })
  @Type(() => MonitoringProductItem)
  attachments?: MonitoringProductItem[];

  private constructor();
  private constructor(params: PatientMonitorConfigurationParams);
  private constructor(params?: PatientMonitorConfigurationParams) {
    if (!params) {
      super("", "", DESERIALIZATION_PROJECT_PLACEHOLDER);
      return;
    }

    super(params.id, params.name, params.project);
    this.category = params.category;
    this.applyDetails(params);
  }

  static create(params: PatientMonitorConfigurationCreateParams): PatientMonitorConfiguration {
    return new PatientMonitorConfiguration({
      id: uuidv4(),
      ...params,
    });
  }

  update(params: PatientMonitorConfigurationDetails) {
    this.applyDetails(params);
    this.project.publishUpdate(ProjectEventType.PATIENT_MONITOR_CONFIGURATION_UPDATED, this);
  }

  private applyDetails(details: PatientMonitorConfigurationDetails) {
    this.name = details.name;
    this.familyNumber = details.familyNumber;
    this.family = details.family;
    this.sensor = details.sensor;
    this.mountingGroup = details.mountingGroup;
    this.mountingArm = details.mountingArm;
    this.attachments = details.attachments;
  }

  getDeviceMatNr() {
    const matNr: string[] = [
      ...(this._family?.option?.devices ?? []),
      ...(this._sensor?.option?.devices ?? []),
      ...(this._mountingGroup?.option?.devices ?? []),
      ...(this.mountingArm?.option?.devices ?? []),
    ];

    for (const attachment of this.attachments ?? []) {
      matNr.push(...attachment.option.devices);
    }
    return matNr;
  }

  override get mainProductId(): string {
    return this.familyNumber;
  }

  get category(): MonitoringCategory {
    return this._category;
  }

  get family(): MonitoringProductItem | undefined {
    return this._family;
  }

  get sensor(): MonitoringProductItem | undefined {
    return this._sensor;
  }

  get mountingGroup(): MonitoringProductItem | undefined {
    return this._mountingGroup;
  }

  get mountingArm(): MonitoringProductItem | undefined {
    return this._mountingArm;
  }

  set category(value: MonitoringCategory) {
    this._category = value;
  }

  set family(value: MonitoringProductItem | undefined) {
    this._family = value;
  }

  set sensor(value: MonitoringProductItem | undefined) {
    this._sensor = value;
  }

  set mountingGroup(value: MonitoringProductItem | undefined) {
    this._mountingGroup = value;
  }

  set mountingArm(value: MonitoringProductItem | undefined) {
    this._mountingArm = value;
  }

  get configuredProductIds(): string[] {
    return [
      ...(this.family?.option?.devices ?? []),
      ...(this.mountingArm?.option?.devices ?? []),
      ...(this.mountingGroup?.option?.devices ?? []),
      ...(this._sensor?.option?.devices ?? []),
      ...this.attachmentDevices,
    ];
  }

  getOptions(): MonitoringProductItem[] {
    return [
      ...(this.family ? [this.family] : []),
      ...(this.sensor ? [this.sensor] : []),
      ...(this.mountingGroup ? [this.mountingGroup] : []),
      ...(this.mountingArm ? [this.mountingArm] : []),
      ...(this.attachments ?? []),
    ];
  }

  getAllItems(): MonitoringProductItem[] {
    return this.getOptions();
  }

  get attachmentDevices(): string[] {
    return this.attachments?.flatMap((attachment) => attachment.option.devices) ?? [];
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedPatientMonitors(this) : 0;
  }
}
